import cornerstone from 'cornerstone-core';

/** Parse DICOM DA (YYYYMMDD) + TM (HHMMSS[.frac]) into a JS Date (UTC). */
export function parseDicomDateTime(da, tm) {
  if (!da || !tm) return null;
  try {
    const daStr = String(da);
    const y = Number(daStr.slice(0, 4));
    const m = Number(daStr.slice(4, 6));
    const d = Number(daStr.slice(6, 8));
    const tmClean = String(tm)
      .split('.')[0]
      .padStart(6, '0');
    const hh = Number(tmClean.slice(0, 2));
    const mm = Number(tmClean.slice(2, 4));
    const ss = Number(tmClean.slice(4, 6));
    return new Date(Date.UTC(y, m - 1, d, hh, mm, ss));
  } catch (error) {
    return null;
  }
}

export function moToSuv(mo, decayCorrectedDoseBq, patientWeightKg) {
  if (!isFinite(mo) || mo < 0) return null;       // mo must be [Bq/ml]
  if (!isFinite(decayCorrectedDoseBq) || decayCorrectedDoseBq <= 0) return null;    //decayCorrectedDoseBq must be [Bq]
  if (!isFinite(patientWeightKg) || patientWeightKg <= 0) return null;
  return (mo * patientWeightKg * 1000) / decayCorrectedDoseBq;    
}

/**
 * Resolve SUV factors for a PET imageId.
 * @returns { ok: true, decayCorrectedDoseBq, patientWeightKg, halfLifeSec, tracer, units, dataSource }
 *        | { ok: false, reason }
 */
export function getPetSuvFactors(petImageId) {
  if (!petImageId) return { ok: false, reason: 'no-image-id' };

  const {patientWeight} =
    cornerstone.metaData.get('patientStudyModule', petImageId) ||
    {};
  const pet =
    cornerstone.metaData.get('petIsotopeModule', petImageId) ||
    {};
  const series =
    cornerstone.metaData.get('generalSeriesModule', petImageId) || {};
  const instance = cornerstone.metaData.get('instance', petImageId) || {};

  /* ----- Units check ----- */
  const units = pet.units || instance.Units || instance['00541001'] || '';
  if (units !== 'BQML') {
    return { ok: false, reason: 'units-not-bqml', units };
  }

  const patientWeightKg = Number(patientWeight);  // type-safe

  // /* ----- Radiopharmaceutical sequence (may be missing under anonymization) ----- */
  const rpItem = pet.radiopharmaceuticalInfo || {};

  const radionuclideTotalDose =
    rpItem?.radionuclideTotalDose ||
    null;

  let halfLifeSec =
    rpItem?.radionuclideHalfLife ||
    null;

  /* ----- Compute decay-corrected dose ----- */
  let decayCorrectedDoseBq;

  if (radionuclideTotalDose) {
    const rpStartDateTime =
      rpItem?.radiopharmaceuticalStartDateTime ||
      null;
    const rpStartTime =
      rpItem?.radiopharmaceuticalStartTime ||
      null;
    const studyDate =
      series.studyDate ||
      series.StudyDate ||
      instance.StudyDate ||
      instance['00080020'];

    const seriesDate =
      series.seriesDate || series.SeriesDate || instance.SeriesDate;
    const seriesTime =
      series.seriesTime || series.SeriesTime || instance.SeriesTime;
    const seriesDateTime = parseDicomDateTime(seriesDate, seriesTime);


    let injectionDateTime = null;
    if (rpStartDateTime) {
      const dt = String(rpStartDateTime).split('.')[0].padStart(14, '0');
        injectionDateTime = parseDicomDateTime(dt.slice(0, 8), dt.slice(8, 14));    
    } else if (rpStartTime && (seriesDate || studyDate)) {
      const dateStr = seriesDate || studyDate; 
      let timeStr = null;
      if (typeof rpStartTime === 'string') {
        timeStr = rpStartTime; 
      } else if (rpStartTime && typeof rpStartTime === 'object') {
        const hh = String(rpStartTime.hours ?? 0).padStart(2, '0');
        const mm = String(rpStartTime.minutes ?? 0).padStart(2, '0');
        const ss = String(rpStartTime.seconds ?? 0).padStart(2, '0');
        timeStr = `${hh}${mm}${ss}`;
      }
      if (timeStr) injectionDateTime = parseDicomDateTime(dateStr, timeStr);
    }
 
    const decayCorrection =
      pet.decayCorrection ||
      instance.DecayCorrection ||
      instance['00541102'] ||
      '';

    if (decayCorrection === 'ADMIN') {
      decayCorrectedDoseBq = radionuclideTotalDose;
    } else if (!halfLifeSec) {
      return { ok: false, reason: 'missing-half-life' };
    } else if (decayCorrection === 'START') {
      const seriesMs = seriesDateTime?.getTime();
      const injMs = injectionDateTime?.getTime();
      if (!Number.isFinite(seriesMs)) {
        return { ok: false, reason: 'invalid-seriesTime' };
      }
      if (!Number.isFinite(injMs)){
        return { ok: false, reason: 'invalid-injectionTime' };
      }

      const elapsedSec =
        (seriesMs - injMs) / 1000;
      if (!Number.isFinite(elapsedSec) || elapsedSec < 0 || elapsedSec > 24 * 3600) {
        return { ok: false, reason: 'implausible-elapsed-time', elapsedSec };
      } else {
        const decayFactor = Math.exp((-Math.log(2) * elapsedSec) / halfLifeSec);
        decayCorrectedDoseBq = radionuclideTotalDose * decayFactor;
      }
    } else {
      return { ok: false, reason: 'decay-correction-invalid' };
    }
  } else {
    return { ok: false, reason: 'missing-injected-dose' };
  }

  if (
    !Number.isFinite(decayCorrectedDoseBq) || decayCorrectedDoseBq <= 0 ||
    !Number.isFinite(patientWeightKg) || patientWeightKg <= 0
  ) {
    return { ok: false, reason: 'invalid-suv-factors' };
  }

  if (!halfLifeSec) {
    return { ok: false, reason: 'missing-half-life' };
  }

  return {
    ok: true,
    decayCorrectedDoseBq,
    patientWeightKg,
    halfLifeSec,
    units,
    dataSource: {
      dose: 'dicom',
      weight: 'dicom',
    },
  };
}